// =============================================================================
// RealPLC v6.5 L5 - Constants
// Merged from original constants.ts
// =============================================================================
import { PlcBrand, PlcLanguage } from '../types/index.js';
export const PLC_BRANDS = [
    { value: PlcBrand.Siemens, label: 'Siemens (TIA Portal)', icon: 'S' },
    { value: PlcBrand.AllenBradley, label: 'Allen-Bradley (Studio 5000)', icon: 'AB' },
    { value: PlcBrand.Beckhoff, label: 'Beckhoff (TwinCAT 3)', icon: 'B' },
    { value: PlcBrand.Codesys, label: 'CODESYS (Generic)', icon: 'C' },
    { value: PlcBrand.Inovance, label: 'Inovance (汇川)', icon: 'I' },
    { value: PlcBrand.ABB, label: 'ABB (Automation Builder)', icon: 'ABB' },
    { value: PlcBrand.Mitsubishi, label: 'Mitsubishi (GX Works3)', icon: 'M' },
    { value: PlcBrand.Omron, label: 'Omron (Sysmac Studio)', icon: 'O' },
];
// The ladder value itself stays 'Ladder Logic (ASCII Representation)': saved
// preferences and session snapshots already hold it. Only its label changes —
// the model never writes ASCII art, it writes ladder text that the server
// renders (docs/superpowers/specs/2026-09-23-tia-ladder-design.md).
export const PLC_LANGUAGES = [
    { value: PlcLanguage.ST, label: 'Structured Text (SCL/ST)' },
    { value: PlcLanguage.LAD, label: 'Ladder Diagram (LAD)', abbreviation: 'LAD' },
];
/** Siemens is the only platform with a ladder path (SimaticML import into TIA Portal). */
export function languagesForBrand(brand) {
    return brand === PlcBrand.Siemens
        ? PLC_LANGUAGES
        : PLC_LANGUAGES.filter(option => option.value === PlcLanguage.ST);
}
/** The language a turn on this platform actually uses: the chosen one if offered, else structured text. */
export function languageForBrand(brand, language) {
    return languagesForBrand(brand).some(option => option.value === language) ? language : PlcLanguage.ST;
}
/**
 * Whether a turn writes ladder: the one test every ladder branch reads, on the
 * server and in the browser. Takes the language as a request carries it — a
 * string that may be stale, from another platform, or missing.
 */
export function writesLadder(brand, language) {
    return languageForBrand(brand, language) === PlcLanguage.LAD;
}
export const AI_PROVIDERS = [
    { value: 'realplc', label: 'RealPLC', baseUrl: '' },
    { value: 'nvidia', label: 'Nvidia', baseUrl: 'https://integrate.api.nvidia.com/v1' },
    { value: 'google', label: 'Google', baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai' },
    { value: 'openai', label: 'OpenAI', baseUrl: 'https://api.openai.com/v1' },
    { value: 'groq', label: 'Groq', baseUrl: 'https://api.groq.com/openai/v1' },
    { value: 'openrouter', label: 'OpenRouter', baseUrl: 'https://openrouter.ai/api/v1' },
    { value: 'deepseek', label: 'DeepSeek', baseUrl: 'https://api.deepseek.com' },
    { value: 'zai', label: 'Z.AI (GLM-5.1)', baseUrl: 'https://api.z.ai/api/paas/v4' },
    { value: 'zhipu', label: 'Zhipu (BigModel)', baseUrl: 'https://open.bigmodel.cn/api/paas/v4' },
    { value: 'moonshot', label: 'Moonshot (Kimi)', baseUrl: 'https://api.moonshot.cn/v1' },
    { value: 'minimax', label: 'MiniMax', baseUrl: 'https://api.minimax.chat/v1' },
    { value: 'aliyun', label: 'Aliyun Bailian', baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1' },
    { value: 'siliconflow', label: 'SiliconFlow', baseUrl: 'https://api.siliconflow.cn/v1' },
    { value: 'baicai', label: 'BaiCai', baseUrl: 'https://llm.develope.cn/claude' },
];
export const AI_MODELS = [
    // RealPLC AutoFree
    { value: 'autofree', label: 'AutoFree', provider: 'realplc' },
    // Nvidia
    { value: 'deepseek-ai/deepseek-v3.2', label: 'DeepSeek V3.2', provider: 'nvidia' },
    { value: 'deepseek-ai/deepseek-r1-distill-llama-8b', label: 'DeepSeek R1 Distill Llama 8B', provider: 'nvidia' },
    { value: 'meta/llama-3.1-405b-instruct', label: 'Llama 3.1 405B', provider: 'nvidia' },
    { value: 'meta/llama-3.1-70b-instruct', label: 'Llama 3.1 70B', provider: 'nvidia' },
    { value: 'moonshotai/kimi-k2.5', label: 'Kimi K2.5', provider: 'nvidia' },
    { value: 'minimaxai/minimax-m2.5', label: 'MiniMax M2.5', provider: 'nvidia' },
    { value: 'openai/gpt-oss-120b', label: 'GPT-OSS 120B', provider: 'nvidia' },
    { value: 'stepfun-ai/step-3.5-flash', label: 'Step-3.5 Flash', provider: 'nvidia' },
    { value: 'qwen/qwen3-coder-480b-a35b-instruct', label: 'Qwen3 Coder 480B', provider: 'nvidia' },
    // Google
    { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro', provider: 'google' },
    { value: 'gemini-3.1-flash', label: 'Gemini 3.1 Flash', provider: 'google' },
    // OpenAI
    { value: 'gpt-5.4', label: 'GPT-5.4', provider: 'openai' },
    { value: 'o3', label: 'o3', provider: 'openai' },
    // DeepSeek
    { value: 'deepseek-v4-flash', label: 'DeepSeek-V4-Flash', provider: 'deepseek' },
    { value: 'deepseek-v4-pro', label: 'DeepSeek-V4-Pro', provider: 'deepseek' },
    // Zhipu (GLM)
    { value: 'glm-5.1', label: 'GLM-5.1', provider: 'zhipu' },
    // Aliyun Bailian
    { value: 'qwen-3.6-plus', label: 'Qwen 3.6 Plus', provider: 'aliyun' },
    { value: 'qwen-max', label: 'Qwen-Max', provider: 'aliyun' },
    { value: 'qwen-flash', label: 'Qwen-Flash', provider: 'aliyun' },
];
// Default AutoFree pool for fast/normal chat.
export const AUTO_FREE_POOL = [
    { provider: 'nvidia', model: 'openai/gpt-oss-20b', comment: 'GPT-OSS 20B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.1-8b-instruct', comment: 'Llama 3.1 via NVIDIA NIM (Fast)' },
    { provider: 'nvidia', model: 'qwen/qwen2.5-coder-32b-instruct', comment: 'Qwen 2.5 via NVIDIA NIM (Coding)' },
    { provider: 'siliconflow', model: 'Qwen/Qwen3.5-4B', comment: 'Qwen 3.5 4B via SiliconFlow (Ultra Fast)' },
    { provider: 'nvidia', model: 'nvidia/nemotron-3-nano-30b-a3b', comment: 'Nemotron-3 Nano 30B A3B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'mistralai/mixtral-8x22b-instruct-v0.1', comment: '排名2，延迟329ms，评分78.1，首轮通过' },
    { provider: 'nvidia', model: 'meta/llama-4-maverick-17b-128e-instruct', comment: '排名5，延迟465ms，评分69.0，首轮通过' },
    { provider: 'nvidia', model: 'nvidia/riva-translate-4b-instruct-v1.1', comment: '排名6，延迟532ms，评分64.5，首轮通过' },
    { provider: 'nvidia', model: 'mistralai/ministral-14b-instruct-2512', comment: '排名7，延迟561ms，评分62.6，首轮通过' },
    { provider: 'nvidia', model: 'nvidia/nvidia-nemotron-nano-9b-v2', comment: '排名8，延迟608ms，评分59.5，首轮通过' },
    { provider: 'nvidia', model: 'nvidia/nemotron-mini-4b-instruct', comment: '排名9，延迟627ms，评分58.2，首轮通过' },
    { provider: 'nvidia', model: 'mistralai/mixtral-8x7b-instruct-v0.1', comment: '排名10，延迟653ms，评分56.5，首轮通过' },
    { provider: 'nvidia', model: 'nvidia/nemotron-nano-12b-v2-vl', comment: '排名15，延迟841ms，评分43.9，首轮通过' },
    { provider: 'nvidia', model: 'mistralai/mistral-small-4-119b-2603', comment: '排名19，延迟895ms，评分40.3，首轮通过' },
    //  { provider: 'nvidia', model: 'sarvamai/sarvam-m', comment: 'Sarvam-M via NVIDIA NIM' },
    // { provider: 'nvidia', model: 'google/gemma-2-2b-it', comment: 'Gemma 2 2B IT via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/nemotron-3-super-120b-a12b', comment: 'Nemotron-3 Super 120B A12B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'openai/gpt-oss-120b', comment: 'GPT-OSS 120B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.1-70b-instruct', comment: 'Llama 3.1 70B Instruct via NVIDIA NIM' },
    { provider: 'nvidia', model: 'google/gemma-3n-e4b-it', comment: 'Gemma 3N E4B IT via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/llama-3.3-nemotron-super-49b-v1.5', comment: 'Llama 3.3 Nemotron Super 49B v1.5 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/llama-3.3-nemotron-super-49b-v1', comment: 'Llama 3.3 Nemotron Super 49B v1 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'mistralai/mistral-large-3-675b-instruct-2512', comment: 'Mistral Large 3 675B Instruct 2512 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'moonshotai/kimi-k2-instruct-0905', comment: 'Kimi K2 Instruct 0905 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'qwen/qwen3-next-80b-a3b-instruct', comment: 'Qwen3 Next 80B A3B Instruct via NVIDIA NIM' },
    { provider: 'nvidia', model: 'qwen/qwen3.5-122b-a10b', comment: 'Qwen3.5 122B A10B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'deepseek-ai/deepseek-v4-flash', comment: 'DeepSeek V4 Flash via NVIDIA NIM' },
    { provider: 'nvidia', model: 'moonshotai/kimi-k2-instruct', comment: 'Kimi K2 Instruct via NVIDIA NIM' },
    { provider: 'nvidia', model: 'google/gemma-3n-e2b-it', comment: 'Gemma 3N E2B IT via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.1-405b-instruct', comment: 'Llama 3.1 405B Instruct via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.2-11b-vision-instruct', comment: 'Llama 3.2 11B Vision Instruct via NVIDIA NIM' },
    { provider: 'nvidia', model: 'mistralai/mistral-nemotron', comment: 'Mistral Nemotron via NVIDIA NIM' },
    { provider: 'nvidia', model: 'moonshotai/kimi-k2-thinking', comment: 'Kimi K2 Thinking via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/ising-calibration-1-35b-a3b', comment: 'Ising Calibration 1 35B A3B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'qwen/qwen3.5-397b-a17b', comment: 'Qwen3.5 397B A17B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'stepfun-ai/step-3.5-flash', comment: 'Step 3.5 Flash via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.3-70b-instruct', comment: 'Llama 3.3 via NVIDIA NIM (Large Context Coding)' },
    { provider: 'nvidia', model: 'qwen/qwen3-coder-480b-a35b-instruct', comment: 'Qwen 3 via NVIDIA NIM (Large Context Coding)' },
    { provider: 'nvidia', model: 'google/gemma-4-31b-it', comment: 'Gemma 4 via NVIDIA NIM (Large Context Coding)' },
    { provider: 'nvidia', model: 'nvidia/llama-3.1-nemotron-ultra-253b-v1', comment: 'Llama 3.1 Nemotron Ultra via NVIDIA NIM' },
    { provider: 'siliconflow', model: 'THUDM/GLM-4-9B-0414', comment: 'GLM 4.1V via SiliconFlow' },
    { provider: 'zhipu', model: 'glm-4.7-flash', comment: 'GLM-4.7 Flash via Zhipu' },
    { provider: 'zhipu', model: 'GLM-4-Flash-250414', comment: 'GLM-4 Flash 250414 via Zhipu' },
];
// Default AutoFree pool for thinking/deep reasoning chat.
export const THINKING_POOL = [
    { provider: 'nvidia', model: 'qwen/qwen3-next-80b-a3b-thinking', comment: '排名1，延迟321ms，评分78.6，首轮通过' },
    // { provider: 'nvidia', model: 'nvidia/nemotron-content-safety-reasoning-4b', comment: '排名14，延迟828ms，评分44.8，首轮通过' },
    { provider: 'nvidia', model: 'qwen/qwen3.5-122b-a10b', comment: 'Qwen 3.5 via NVIDIA NIM (High Intelligence)' },
    { provider: 'nvidia', model: 'qwen/qwen3-coder-480b-a35b-instruct', comment: 'Qwen 3 via NVIDIA NIM (Large Context Coding)' },
    { provider: 'nvidia', model: 'deepseek-ai/deepseek-v4-pro', comment: 'DeepSeek V4 Flash via NVIDIA NIM' },
    { provider: 'nvidia', model: 'z-ai/glm5.1', comment: 'GLM-4.7 via NVIDIA NIM (Deep Reasoning Mode)' },
    { provider: 'siliconflow', model: 'deepseek-ai/DeepSeek-R1-0528-Qwen3-8B', comment: 'DeepSeek R1 via SiliconFlow (Deep Reasoning)' },
    // { provider: 'siliconflow', model: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B', comment: 'DeepSeek R1 Distill  (Fast Reasoning)' },
    { provider: 'nvidia', model: 'nvidia/nemotron-3-nano-30b-a3b', comment: 'Nemotron-3 Nano 30B A3B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/nemotron-3-super-120b-a12b', comment: 'Nemotron-3 Super 120B A12B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/nemotron-3-nano-omni-30b-a3b-reasoning', comment: '排名3，延迟367ms，评分75.5，首轮通过' },
    { provider: 'nvidia', model: 'mistralai/mistral-small-4-119b-2603', comment: 'Mistral Small 4 119B 2603 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'openai/gpt-oss-120b', comment: 'GPT-OSS 120B via NVIDIA NIM' },
    // { provider: 'nvidia', model: 'nvidia/nemotron-content-safety-reasoning-4b', comment: 'GPT-OSS 120B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.1-70b-instruct', comment: 'Llama 3.1 70B Instruct via NVIDIA NIM' },
    { provider: 'nvidia', model: 'google/gemma-3n-e4b-it', comment: 'Gemma 3N E4B IT via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/llama-3.3-nemotron-super-49b-v1.5', comment: 'Llama 3.3 Nemotron Super 49B v1.5 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/llama-3.3-nemotron-super-49b-v1', comment: 'Llama 3.3 Nemotron Super 49B v1 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'mistralai/mistral-large-3-675b-instruct-2512', comment: 'Mistral Large 3 675B Instruct 2512 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'moonshotai/kimi-k2-instruct-0905', comment: 'Kimi K2 Instruct 0905 via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.1-8b-instruct', comment: 'Llama 3.1 8B Instruct via NVIDIA NIM' },
    { provider: 'nvidia', model: 'mistralai/mistral-nemotron', comment: 'Mistral Nemotron via NVIDIA NIM' },
    { provider: 'nvidia', model: 'moonshotai/kimi-k2-thinking', comment: 'Kimi K2 Thinking via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/nemotron-nano-12b-v2-vl', comment: 'Nemotron Nano 12B V2 VL via NVIDIA NIM' },
    { provider: 'nvidia', model: 'qwen/qwen3.5-397b-a17b', comment: 'Qwen3.5 397B A17B via NVIDIA NIM' },
    { provider: 'nvidia', model: 'nvidia/llama-3.1-nemotron-ultra-253b-v1', comment: 'Llama 3.1 Nemotron Ultra via NVIDIA NIM' },
    { provider: 'nvidia', model: 'meta/llama-3.3-70b-instruct', comment: 'Llama 3.3 via NVIDIA NIM (Large Context Coding)' },
    { provider: 'nvidia', model: 'meta/llama-3.1-405b-instruct', comment: 'Llama 3.1 via NVIDIA NIM (Large Context Coding)' },
    { provider: 'nvidia', model: 'mistralai/devstral-2-123b-instruct-2512', comment: 'Devstral 2 via NVIDIA NIM (High Intelligence)' },
    { provider: 'nvidia', model: 'deepseek-ai/deepseek-v3.2', comment: 'DeepSeek R1 Distill via NVIDIA NIM' },
    { provider: 'nvidia', model: 'google/gemma-4-31b-it', comment: 'Gemma 4 via NVIDIA NIM (High Intelligence)' },
    { provider: 'zhipu', model: 'glm-4.7-flash', comment: 'GLM-4.7 Flash via Zhipu (Thinking)' },
    { provider: 'zhipu', model: 'GLM-4.1V-Thinking-Flash', comment: 'GLM-4.1V Thinking Flash via Zhipu' },
];
// -----------------------------------------------------------------------------
// Sample Prompts
// -----------------------------------------------------------------------------
export const SAMPLE_PROMPTS = [
    "Control a conveyor belt with start/stop buttons, a safety e-stop, and a 10s timeout if the sensor isn't triggered.",
    "Create a PID temperature control loop for a heating element with anti-windup.",
    "Implement a traffic light sequence for a 4-way intersection using timers and states.",
    "Write a function block to handle a motor starter with overload protection and feedback monitoring.",
    "Control a liquid tank with inlet/outlet valves, level sensors, and start/stop buttons.",
    "Control a multi-step batch recipe process including ingredient dosing, mixing, and discharging.",
    "Implement an alarm handler with priority levels, acknowledgment, auto-reset, and horn control.",
    "Create a circular data logger to record analog values and timestamps into a buffer.",
    "Automate a packaging line including filling, sealing, labeling, and rejection functions.",
    "Control a heating/cooling HVAC system with zone temperatures, fan speeds, and scheduling."
];
// -----------------------------------------------------------------------------
// Validation Constants
// -----------------------------------------------------------------------------
export const MAX_REPAIR_ATTEMPTS = 3;
export const DEFAULT_TIMEOUT_MS = 30000;
export const MAX_CODE_LENGTH = 50000;
/**
 * The longest custom instructions (高级提示词) a turn takes, in characters. They reach the model call with every turn,
 * so the gateway refuses a request with longer ones, and the settings box will not hold more. Generous on purpose:
 * it stops absurd inputs, it is no product limit.
 */
export const MAX_CUSTOM_INSTRUCTION_CHARS = 20_000;
// -----------------------------------------------------------------------------
// Turn Alarm Constants
// -----------------------------------------------------------------------------
/**
 * Past either of these, a turn files an alarm: the runtime writes a diagnostic
 * event on it, and the admin session list marks its conversation red. On
 * 2026-10-20 one turn's classification made 393 attempts in 26 s against its
 * conversation's two request slots; in the two weeks before, a turn made at
 * most 32. Named here because the runtime files the alarm and the gateway and
 * the admin console count it.
 */
export const TURN_ALARM_MODEL_ATTEMPTS = 50;
/** The longest one request of a turn may wait for a request slot before the turn files an alarm. */
export const TURN_ALARM_CAPACITY_WAIT_MS = 10_000;
// -----------------------------------------------------------------------------
// Model Scorecard Constants
// -----------------------------------------------------------------------------
/**
 * Which corpus a scorecard run scores its models against.
 *
 * `golden` is this product's own prompt corpus, shared with the plan-agent
 * regression. `agents4plc_v1` is the published benchmark kept as a control in
 * `knowledge/cases/public/` — a public set can be in a model's training data,
 * so its numbers are read beside the golden ones, never instead of them.
 * `textbook_v1` is ten course-style machines the runtime holds beside the
 * golden prompts: several devices of one kind, alarms that latch, a sequence
 * that waits for feedback, a door interlock.
 *
 * Named here because the gateway queues the run and the runtime executes it,
 * and a set one of them does not know is a run that scores the wrong corpus.
 */
export const MODEL_SCORECARD_SCENARIO_SETS = ['golden', 'agents4plc_v1', 'textbook_v1'];
/**
 * Most scenarios a run may ask for, per set. The runner clamps again to what
 * the corpus actually holds; this is what the panel offers and estimates.
 */
export const MODEL_SCORECARD_SCENARIO_LIMITS = {
    golden: 6,
    agents4plc_v1: 23,
    textbook_v1: 10,
};
// -----------------------------------------------------------------------------
// Upload Constants
// -----------------------------------------------------------------------------
export const FILE_UPLOAD_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;
export const FILE_UPLOAD_SIMPLE_MAX_BYTES = FILE_UPLOAD_CHUNK_SIZE_BYTES;
export const FILE_UPLOAD_MAX_BYTES = 500 * 1024 * 1024;
/**
 * How large one file attached to a conversation may be. The gateway reads
 * such a file whole, and what a model is shown of it is 12,000 tokens.
 */
export const CHAT_FILE_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
/**
 * The size limit of an upload, by where it is made. The knowledge library
 * keeps FILE_UPLOAD_MAX_BYTES. Every other upload is a conversation's, which
 * is also what the server takes an upload that names no entry for.
 */
export function fileUploadMaxBytes(entry) {
    return entry === 'engineering_knowledge' || entry === 'global_submit'
        ? FILE_UPLOAD_MAX_BYTES
        : CHAT_FILE_UPLOAD_MAX_BYTES;
}
/**
 * How many files one conversation holds. They are a set: every turn that
 * continues the conversation is given all of them, so a turn can add to them
 * only while there is room.
 */
export const CONVERSATION_FILE_LIMIT = 10;
// .db, .udt and .awl are the other three files TIA Portal writes when it
// generates sources from blocks, beside .scl; they are text.
export const UPLOAD_ALLOWED_EXTENSIONS = [
    '.txt', '.md', '.csv', '.tsv', '.json', '.xml',
    '.st', '.scl', '.db', '.udt', '.awl', '.l5x', '.l5k', '.log', '.ini',
    '.pdf',
    '.docx', '.xlsx', '.pptx',
    '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp',
];
/**
 * The old Office formats, each with what to save it as. Nothing on the
 * production server reads them: the converter that could is not deployed
 * there. So they are refused when chosen, with the way out, and not after
 * they have been uploaded whole.
 */
const LEGACY_OFFICE_SUCCESSORS = {
    '.doc': '.docx',
    '.xls': '.xlsx',
    '.ppt': '.pptx',
};
/** What an old Office file is to be saved as. Undefined for any other file. */
export function legacyOfficeSuccessor(fileName) {
    const name = fileName.trim().toLowerCase();
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? LEGACY_OFFICE_SUCCESSORS[name.slice(dot)] : undefined;
}
/** What the user is told of an old Office file, by the page before an upload and by the server at one. */
export const UPLOAD_LEGACY_OFFICE_MESSAGE = 'Old Office files (.doc, .xls, .ppt) are not read. Save the file as .docx, .xlsx or .pptx and upload it again.';
export const UPLOAD_IMAGE_EXTENSIONS = [
    '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp',
];
export const UPLOAD_TEXT_CONTEXT_EXTENSIONS = [
    '.pdf', '.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.csv', '.tsv',
    '.txt', '.md', '.json', '.xml', '.st', '.scl', '.db', '.udt', '.awl', '.l5x', '.l5k', '.log', '.ini',
];
// What the file chooser offers. The old Office formats are offered too: a file
// that cannot be chosen is a file the user is told nothing about.
export const UPLOAD_FILE_ACCEPT = [...UPLOAD_ALLOWED_EXTENSIONS, ...Object.keys(LEGACY_OFFICE_SUCCESSORS)].join(',');
/** How many pages of one PDF are read. A manual of a thousand pages is not a requirement. */
export const UPLOAD_PDF_MAX_PAGES = 200;
/**
 * Why an uploaded file could not be read, or was read only in part, in the
 * words shown on the file's card. The parser writes these sentences and the
 * page translates them, so the two have to be the same strings: a warning
 * that is not in this list is some library's own and is not shown to anyone.
 */
export const UPLOAD_READ_MESSAGES = {
    noText: 'No readable text was found in this file.',
    pdfScanned: 'This PDF is a scan, so its text cannot be read. Upload the original Word or Excel file, or type the key content.',
    pdfUndecodable: 'The text of this PDF could not be decoded. Upload the original Word or Excel file, or paste the text.',
    pdfPagesNotRead: `Only the first ${UPLOAD_PDF_MAX_PAGES} pages of this PDF were read.`,
    pdfTooSlow: 'This PDF took too long to read. Upload the part you need, or paste the text.',
    notText: 'This file is not readable text.',
    encodingNotRecognised: 'The text encoding of this file was not recognised. Save it as UTF-8 and upload it again.',
    legacyOffice: 'Old Office formats (.doc, .ppt) cannot be converted on this server. Save the file as .docx or .pptx and upload it again.',
    imagesNotRead: 'Pictures inside this document were not read.',
    columnsNotRead: 'Columns after the 256th of a sheet were not read.',
};
// -----------------------------------------------------------------------------
// Routing Constants
// -----------------------------------------------------------------------------
export const LATENCY_STICKY_THRESHOLD_MS = 1500;
export const MODEL_COOLDOWN_MS = 60000;
export const AUTOFREE_RETRY_DELAY_MS = 1000;
export const TASK_TYPE_LABELS = {
    // detectTaskType 的全部返回值
    'conveyor-control': { en: 'Conveyor', zh: '传送带' },
    'process-control': { en: 'Process', zh: '过程温控' },
    'motor-control': { en: 'Motor', zh: '电机' },
    'tank-control': { en: 'Level', zh: '液位' },
    'batch-control': { en: 'Batch', zh: '配料' },
    'alarm-handling': { en: 'Alarm', zh: '报警' },
    'traffic-control': { en: 'Traffic', zh: '交通灯' },
    'packaging-control': { en: 'Packaging', zh: '包装' },
    'hvac-control': { en: 'HVAC', zh: '暖通' },
    'data-logging': { en: 'DataLog', zh: '数据记录' },
    'logic-control': { en: 'Logic', zh: '逻辑' },
    'general-control': { en: 'System', zh: '系统' },
    // 场景模板另外用到的几个
    'sequential-control': { en: 'Sequence', zh: '顺控' },
    'supervisory-control': { en: 'Supervisory', zh: '监控' },
    'data-management': { en: 'DataManagement', zh: '数据管理' },
    'environmental-control': { en: 'Environment', zh: '环境' },
};
export const CONTROL_STRATEGY_LABELS = {
    // detectControlStrategy 的全部返回值
    sequential: { en: 'Sequential', zh: '顺序控制' },
    'state-based': { en: 'StateBased', zh: '状态控制' },
    'closed-loop': { en: 'ClosedLoop', zh: '闭环控制' },
    'timer-based': { en: 'TimerBased', zh: '定时控制' },
    'threshold-control': { en: 'Threshold', zh: '双位控制' },
    cascade: { en: 'Cascade', zh: '串级控制' },
    'ratio-control': { en: 'Ratio', zh: '配比控制' },
    'basic-control': { en: 'Basic', zh: '基本控制' },
};
//# sourceMappingURL=index.js.map